import React from "react";
import { Box, Menu, MenuItem, MenuList, Typography, useTheme } from "@mui/material";
import { ArrowDownwardRounded, ArrowUpwardRounded } from "@mui/icons-material";
import BottomSheet from "../../common/BottomSheet";
import { useTouchLayout } from "../../../hooks/useTouchLayout";
import { hapticSelection } from "../../../lib/haptics";
import { focusRingSx, panelBorder, panelHairline } from "../overviewPanel";
import { touchHitAreaSx } from "../EarningsCalendar/touchHitArea";
import type { PulseMetric } from "../CompanyPulseCard";
import {
  DEFAULT_SORT_DIR,
  WatchlistSortDir,
  WatchlistSortKey,
} from "./sortWatchlistEntries";

/**
 * Picks how the active watchlist is sorted and which metric its cards show.
 * Anchored menu on pointer, bottom sheet on touch — both wearing the
 * dashboard's own surface (the Radar menu recipe: #212121 paper, hairline
 * border, no shadow), not the generic popover kit.
 *
 * NO header stack (Ben, 2026-09-01): the sort icon the user just pressed is
 * the context, the green arrow on the selected row says "sorting", and the
 * Show choice is a segmented %-$-Cap pill (the Cap/Equal idiom) beside one
 * tiny SHOW label — the design carries what three stacked labels used to.
 *
 * The selected sort row wears its direction as the arrow, and tapping it
 * again FLIPS the direction — the table-header gesture, so asc/desc costs
 * no extra control. A fresh key starts from its natural direction (values
 * biggest-first, letters A-first). The surface stays open while the user
 * fiddles; only the backdrop closes it, so comparing two sorts is cheap.
 */

const SORT_OPTIONS: { key: WatchlistSortKey; label: string }[] = [
  { key: "manual", label: "Manual" },
  { key: "change", label: "Price change" },
  { key: "changePct", label: "Percentage change" },
  { key: "marketCap", label: "Market cap" },
  { key: "symbol", label: "Symbol" },
  { key: "name", label: "Name" },
];

// The pill's labels are the metrics' own shorthand — instantly legible at
// pill size where the full words wouldn't be.
const SHOW_PILL_HEIGHT = 28;

const SHOW_OPTIONS: { key: PulseMetric; label: string }[] = [
  { key: "changePct", label: "%" },
  { key: "change", label: "$" },
  { key: "marketCap", label: "Cap" },
];

interface WatchlistSortMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  sort: WatchlistSortKey;
  dir: WatchlistSortDir;
  show: PulseMetric;
  onSortChange: (sort: WatchlistSortKey, dir: WatchlistSortDir) => void;
  onShowChange: (show: PulseMetric) => void;
}

const WatchlistSortMenu: React.FC<WatchlistSortMenuProps> = ({
  open,
  anchorEl,
  onClose,
  sort,
  dir,
  show,
  onSortChange,
  onShowChange,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isTouch = useTouchLayout();

  const handleSortClick = (key: WatchlistSortKey) => {
    hapticSelection();
    if (key === sort) {
      onSortChange(key, dir === "asc" ? "desc" : "asc");
      return;
    }
    onSortChange(key, DEFAULT_SORT_DIR[key]);
  };

  const selectedBg = isDark ? "rgba(34, 197, 94, 0.08)" : "rgba(34, 197, 94, 0.06)";
  const hoverBg = isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)";

  // Real menu items, so the surface's role="menu" gets the arrow-key
  // traversal and item semantics it promises. One-of-many, so each row is
  // a radio item and the selected one says so.
  const content = (
    <>
      {SORT_OPTIONS.map((option) => {
        const selected = option.key === sort;
        return (
          <MenuItem
            key={option.key}
            role="menuitemradio"
            aria-checked={selected}
            selected={selected}
            disableRipple
            onClick={() => handleSortClick(option.key)}
            sx={{
              justifyContent: "space-between",
              gap: 1.5,
              minHeight: isTouch ? 44 : 38,
              px: 1.25,
              py: 0,
              borderRadius: 1,
              fontSize: "0.8125rem",
              fontWeight: selected ? 600 : 500,
              color: selected ? "#22c55e" : theme.palette.text.secondary,
              transition: "color 0.15s ease, background-color 0.15s ease",
              WebkitTapHighlightColor: "transparent",
              "&:hover": {
                color: selected ? "#22c55e" : theme.palette.text.primary,
                backgroundColor: hoverBg,
              },
              "&.Mui-focusVisible": {
                backgroundColor: hoverBg,
                outline: "2px solid #22c55e",
                outlineOffset: -2,
              },
              "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
                backgroundColor: selectedBg,
              },
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            {option.label}
            {selected && dir === "desc" && (
              <ArrowDownwardRounded sx={{ fontSize: 15, color: "#22c55e", flexShrink: 0 }} />
            )}
            {selected && dir === "asc" && (
              <ArrowUpwardRounded sx={{ fontSize: 15, color: "#22c55e", flexShrink: 0 }} />
            )}
          </MenuItem>
        );
      })}

      <Box sx={{ my: 1, borderTop: `1px solid ${panelHairline(isDark)}` }} />

      {/* One quiet label, one house pill — the whole Show choice in a row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: 1.25,
          pb: 0.25,
          minHeight: isTouch ? 44 : 38,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.625rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: theme.palette.text.secondary,
          }}
        >
          Show
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.25,
            p: 0.25,
            backgroundColor: isDark ? "#2a2a2a" : "#f5f5f5",
            border: `1px solid ${panelBorder(isDark)}`,
            borderRadius: 1,
            flexShrink: 0,
          }}
        >
          {SHOW_OPTIONS.map((option) => {
            const selected = option.key === show;
            return (
              <Box
                key={option.key}
                component="button"
                aria-pressed={selected}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  if (selected) return;
                  hapticSelection();
                  onShowChange(option.key);
                }}
                sx={{
                  all: "unset",
                  ...focusRingSx,
                  // 28px to look at, 44px tall to hit on touch
                  ...(isTouch && touchHitAreaSx(SHOW_PILL_HEIGHT)),
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "0.75rem",
                  fontWeight: selected ? 600 : 500,
                  color: selected ? "#22c55e" : theme.palette.text.secondary,
                  px: 1,
                  height: SHOW_PILL_HEIGHT,
                  minWidth: 22,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 0.75,
                  backgroundColor: selected
                    ? isDark
                      ? "rgba(34,197,94,0.08)"
                      : "rgba(34,197,94,0.06)"
                    : "transparent",
                  transition: "color 200ms linear, background-color 200ms linear",
                  WebkitTapHighlightColor: "transparent",
                  fontVariantNumeric: "tabular-nums",
                  "&:hover": { color: selected ? "#22c55e" : theme.palette.text.primary },
                }}
              >
                {option.label}
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );

  if (isTouch) {
    // The sheet has no menu of its own; the list supplies the role the
    // items expect.
    return (
      <BottomSheet open={open} onClose={onClose}>
        <MenuList disablePadding>{content}</MenuList>
      </BottomSheet>
    );
  }

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      elevation={0}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        // The dashboard's own menu surface (the Radar rail-menu recipe) —
        // hairline-edged, flat, on the section's raised paper tone.
        paper: {
          sx: {
            mt: 0.5,
            minWidth: 236,
            backgroundColor: isDark ? "#212121" : "#ffffff",
            backgroundImage: "none",
            border: `1px solid ${panelBorder(isDark)}`,
            borderRadius: 1.5,
            boxShadow: "none",
          },
        },
        list: { sx: { p: 0.75 } },
      }}
    >
      {content}
    </Menu>
  );
};

export default WatchlistSortMenu;
